import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import ParentLayout from '../../components/layout/ParentLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { PARENT_CHILDREN, PARENT_DASHBOARD } from '../../config/Config';
import {
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarMonthIcon,
  ChecklistRounded as ChecklistRoundedIcon,
  FamilyRestroom as FamilyRestroomIcon,
  Groups2Rounded as Groups2RoundedIcon,
  InsightsRounded as InsightsRoundedIcon,
  LaunchRounded as LaunchRoundedIcon,
  PersonRounded as PersonRoundedIcon,
  SchoolRounded as SchoolRoundedIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const shellCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: 'rgba(63, 81, 181, 0.12)',
  boxShadow: '0 12px 32px rgba(20, 32, 74, 0.08)',
  background: '#fff',
};

const ParentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    total_children: 0,
    active_tests: 0,
    pending_assignments: 0,
    upcoming_deadlines: 0,
    awards_given: 0,
    recent_activity: [],
  });
  const [children, setChildren] = useState([]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await api.get(PARENT_DASHBOARD);
        setDashboardData({
          total_children: response.data?.total_children || 0,
          active_tests: response.data?.active_tests || 0,
          pending_assignments: response.data?.pending_assignments || 0,
          upcoming_deadlines: response.data?.upcoming_deadlines || 0,
          awards_given: response.data?.awards_given || 0,
          recent_activity: response.data?.recent_activity || [],
        });
      } catch (error) {
        console.error('Error fetching parent dashboard data:', error);
        setDashboardData({
          total_children: 0,
          active_tests: 0,
          pending_assignments: 0,
          upcoming_deadlines: 0,
          awards_given: 0,
          recent_activity: [],
        });
      }
    };

    loadDashboard();
  }, []);

  useEffect(() => {
    const loadChildren = async () => {
      try {
        const response = await api.get(PARENT_CHILDREN);
        setChildren(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching parent children:', error);
        setChildren([]);
      }
    };

    loadChildren();
  }, []);

  const linkedChildren = useMemo(
    () => children.filter((child) => child.status === 'active'),
    [children]
  );

  const priorityConfig = useMemo(() => {
    if (!dashboardData.total_children) {
      return {
        eyebrow: 'Get started',
        title: 'Your family dashboard is ready for its first child',
        body: 'Add your child first so you can track assignments, follow progress, and keep every update in one place.',
        ctaLabel: 'My Children',
        ctaPath: '/parent/children',
        chips: [
          { label: '0 linked children' },
          { label: `${dashboardData.pending_assignments} assignments to review` },
        ],
      };
    }

    if (dashboardData.pending_assignments > 0) {
      return {
        eyebrow: 'Needs attention',
        title: `${dashboardData.pending_assignments} assignment${dashboardData.pending_assignments === 1 ? '' : 's'} need attention`,
        body: 'Check what is still pending so your child can stay on track with class expectations and upcoming work.',
        ctaLabel: 'Assignments',
        ctaPath: '/parent/assignments',
        chips: [
          { label: `${dashboardData.total_children} linked child${dashboardData.total_children === 1 ? '' : 'ren'}` },
          { label: `${dashboardData.upcoming_deadlines} upcoming deadline${dashboardData.upcoming_deadlines === 1 ? '' : 's'}` },
        ],
      };
    }

    if (dashboardData.active_tests > 0) {
      return {
        eyebrow: 'Progress to review',
        title: `${dashboardData.active_tests} active test${dashboardData.active_tests === 1 ? '' : 's'} are underway`,
        body: 'Open progress reports to see how your child is performing and where extra support may help most.',
        ctaLabel: 'Progress Reports',
        ctaPath: '/parent/progress',
        chips: [
          { label: `${dashboardData.total_children} linked child${dashboardData.total_children === 1 ? '' : 'ren'}` },
          { label: `${dashboardData.awards_given} award${dashboardData.awards_given === 1 ? '' : 's'} earned` },
        ],
      };
    }

    return {
      eyebrow: 'All caught up',
      title: 'Everything looks calm right now',
      body: 'You are up to date on assignments. Keep an eye on progress reports for the next round of activity.',
      ctaLabel: 'Progress Reports',
      ctaPath: '/parent/progress',
      chips: [
        { label: `${dashboardData.total_children} linked child${dashboardData.total_children === 1 ? '' : 'ren'}` },
        { label: `${dashboardData.upcoming_deadlines} upcoming deadline${dashboardData.upcoming_deadlines === 1 ? '' : 's'}` },
      ],
    };
  }, [dashboardData]);

  const statCards = [
    {
      title: 'Linked children',
      value: dashboardData.total_children,
      helper: dashboardData.total_children ? 'Family members you can monitor here' : 'Add your first child to get started',
      icon: <Groups2RoundedIcon />,
      accent: '#4f64d9',
    },
    {
      title: 'Active tests',
      value: dashboardData.active_tests,
      helper: dashboardData.active_tests ? 'Tests currently in progress or available' : 'No active tests right now',
      icon: <SchoolRoundedIcon />,
      accent: '#d74f8a',
    },
    {
      title: 'Pending assignments',
      value: dashboardData.pending_assignments,
      helper: dashboardData.pending_assignments ? 'Work still waiting to be completed' : 'No pending work at the moment',
      icon: <AssignmentIcon />,
      accent: '#f59b23',
    },
    {
      title: 'Upcoming deadlines',
      value: dashboardData.upcoming_deadlines,
      helper: dashboardData.upcoming_deadlines ? 'Deadlines coming up soon' : 'Nothing due soon',
      icon: <CalendarMonthIcon />,
      accent: '#1696d2',
    },
  ];

  const actionCards = [
    {
      title: 'My Children',
      helper: 'See linked children and manage access',
      path: '/parent/children',
      icon: <FamilyRestroomIcon />,
    },
    {
      title: 'Assignments',
      helper: 'Check pending work and deadlines',
      path: '/parent/assignments',
      icon: <ChecklistRoundedIcon />,
    },
    {
      title: 'Progress Reports',
      helper: 'View performance insights for your own children',
      path: '/parent/progress',
      icon: <InsightsRoundedIcon />,
    },
  ];

  const childSummaryItems = linkedChildren.slice(0, 4);

  return (
    <ParentLayout>
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <Paper
          sx={{
            ...shellCardSx,
            mb: 4,
            p: { xs: 3, md: 4 },
            background:
              'radial-gradient(circle at top left, rgba(79, 100, 217, 0.14), transparent 36%), linear-gradient(135deg, rgba(243, 247, 255, 0.95), #ffffff 58%)',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={8}>
              <Stack spacing={2}>
                <Chip
                  label={priorityConfig.eyebrow}
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(79, 100, 217, 0.12)',
                    color: 'primary.main',
                    fontWeight: 700,
                  }}
                />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, mb: 1.5, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    Welcome back, {user?.first_name || 'Parent'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.18, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    {priorityConfig.title}
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, fontSize: '1.05rem' }}>
                  {priorityConfig.body}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Stack spacing={2} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => navigate(priorityConfig.ctaPath)}
                  endIcon={<LaunchRoundedIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.4,
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: '0 14px 28px rgba(79, 100, 217, 0.22)',
                  }}
                >
                  {priorityConfig.ctaLabel}
                </Button>
                <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                  {priorityConfig.chips.map((chip) => (
                    <Chip
                      key={chip.label}
                      label={chip.label}
                      sx={{
                        borderRadius: 999,
                        bgcolor: '#fff',
                        border: '1px solid rgba(79, 100, 217, 0.16)',
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            Family snapshot
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A quick view of what is active, pending, and coming up across your linked children.
          </Typography>
          <Grid container spacing={2.5}>
            {statCards.map((card) => (
              <Grid item xs={12} sm={6} xl={3} key={card.title}>
                <Paper
                  sx={{
                    ...shellCardSx,
                    height: '100%',
                    p: 2.5,
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 18px 36px rgba(20, 32, 74, 0.12)',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                        {card.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.helper}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        bgcolor: `${card.accent}1A`,
                        color: card.accent,
                        width: 52,
                        height: 52,
                      }}
                    >
                      {card.icon}
                    </Avatar>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={7}>
            <Paper sx={{ ...shellCardSx, p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                My children
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Only children linked to your family account appear here and in progress reports.
              </Typography>

              {childSummaryItems.length > 0 ? (
                <Grid container spacing={2}>
                  {childSummaryItems.map((child) => {
                    const fullName = [child.first_name, child.last_name].filter(Boolean).join(' ').trim() || child.email;
                    return (
                      <Grid item xs={12} sm={6} key={child.relationship_id || child.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            p: 2,
                            height: '100%',
                            borderColor: 'rgba(63, 81, 181, 0.14)',
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ bgcolor: 'rgba(79, 100, 217, 0.14)', color: 'primary.main' }}>
                              <PersonRoundedIcon />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                                {fullName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {child.email}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip
                            label={child.status === 'active' ? 'Linked' : 'Pending'}
                            size="small"
                            sx={{
                              mt: 2,
                              bgcolor: child.status === 'active' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(245, 155, 35, 0.14)',
                              color: child.status === 'active' ? 'success.dark' : 'warning.dark',
                              fontWeight: 700,
                            }}
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderStyle: 'dashed',
                    borderColor: 'rgba(63, 81, 181, 0.18)',
                    p: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    No children linked yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your child first to view assignments and performance reports from this dashboard.
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper sx={{ ...shellCardSx, p: { xs: 2.5, md: 3 }, height: '100%' }}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                What to watch
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                A simple read on what matters most across your children right now.
              </Typography>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, borderColor: 'rgba(63, 81, 181, 0.14)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Assignments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dashboardData.pending_assignments > 0
                      ? `${dashboardData.pending_assignments} assignment${dashboardData.pending_assignments === 1 ? '' : 's'} still need attention.`
                      : 'No pending assignments right now.'}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, borderColor: 'rgba(63, 81, 181, 0.14)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Progress reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dashboardData.active_tests > 0
                      ? `Performance data is ready across ${dashboardData.active_tests} active test${dashboardData.active_tests === 1 ? '' : 's'}.`
                      : 'Open progress reports to check the latest completed work once activity begins.'}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, borderColor: 'rgba(63, 81, 181, 0.14)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Deadlines
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dashboardData.upcoming_deadlines > 0
                      ? `${dashboardData.upcoming_deadlines} deadline${dashboardData.upcoming_deadlines === 1 ? '' : 's'} are coming up soon.`
                      : 'There are no upcoming deadlines right now.'}
                  </Typography>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            Take action
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Jump straight to the parent tools that matter most.
          </Typography>
          <Grid container spacing={2.5}>
            {actionCards.map((action) => (
              <Grid item xs={12} md={4} key={action.title}>
                <Paper
                  onClick={() => navigate(action.path)}
                  sx={{
                    ...shellCardSx,
                    p: 2.5,
                    cursor: 'pointer',
                    height: '100%',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 18px 36px rgba(20, 32, 74, 0.12)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'rgba(79, 100, 217, 0.14)', color: 'primary.main', width: 52, height: 52 }}>
                      {action.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {action.helper}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper sx={{ ...shellCardSx, p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            Recent activity
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Activity will appear here as your linked children complete work.
          </Typography>
          {dashboardData.recent_activity?.length ? (
            <Stack spacing={1.5}>
              {dashboardData.recent_activity.map((activity, index) => (
                <Paper
                  key={activity.id || index}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 2,
                    borderColor: 'rgba(63, 81, 181, 0.14)',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activity.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activity.time}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderStyle: 'dashed',
                borderColor: 'rgba(63, 81, 181, 0.18)',
                p: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                No recent activity yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once your child starts working through assignments or tests, updates will appear here alongside progress reports.
              </Typography>
            </Paper>
          )}
        </Paper>
      </Container>
    </ParentLayout>
  );
};

const ParentDashboardWithErrorBoundary = (props) => (
  <ErrorBoundary>
    <ParentDashboard {...props} />
  </ErrorBoundary>
);

export default ParentDashboardWithErrorBoundary;
